import { useEffect, useMemo, useState } from 'react';
import * as perfilService from './perfilService';
import * as funcionarioService from './service';

/**
 * Ao selecionar um Perfil, busca seus dados (nome/CPF/nascimento/e-mail/telefone) só para exibição
 * — esses campos pertencem ao Perfil, não à ficha de Funcionário, então não fazem parte do
 * formulário/schema e nunca são enviados no submit.
 *
 * A lista de opções (`perfilOptions`) exclui Perfis inativos e Perfis que já possuem uma ficha de
 * Funcionário (regra também aplicada pelo backend em FuncionarioController.store), exceto o Perfil
 * atualmente selecionado — que precisa continuar aparecendo para exibir seu label na edição.
 * @param {Object} params
 * @param {string} params.idPerfil Valor atual do campo `idPerfil` do formulário.
 */
function usePerfilAutoFill({ idPerfil }) {
  const [perfis, setPerfis] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [perfilData, setPerfilData] = useState(null);
  const [loadingPerfil, setLoadingPerfil] = useState(false);

  useEffect(() => {
    perfilService
      .findAllPerfis()
      .then(response => setPerfis(response?.data || []))
      .catch(() => setPerfis([]));

    funcionarioService
      .findAll()
      .then(response => setFuncionarios(response?.data || []))
      .catch(() => setFuncionarios([]));
  }, []);

  const idsPerfilComFuncionario = useMemo(
    () => new Set(funcionarios.map(funcionario => String(funcionario.idPerfil))),
    [funcionarios],
  );

  const perfilOptions = useMemo(
    () => [
      { value: '', label: 'Selecione um perfil' },
      ...perfis
        .filter(perfil => {
          const value = String(perfil.id);
          if (value === String(idPerfil)) return true;
          if (perfil.ativo === false) return false;
          return !idsPerfilComFuncionario.has(value);
        })
        .map(perfil => ({
          value: String(perfil.id),
          label: perfil?.usuario?.nome || `Perfil #${perfil.id}`,
        })),
    ],
    [perfis, idsPerfilComFuncionario, idPerfil],
  );

  useEffect(() => {
    if (!idPerfil) {
      setPerfilData(null);
      return;
    }

    let active = true;
    setLoadingPerfil(true);

    perfilService
      .findPerfilById(idPerfil)
      .then(response => {
        if (!active) return;
        setPerfilData(response?.data || null);
      })
      .finally(() => {
        active && setLoadingPerfil(false);
      });

    return () => {
      active = false;
    };
  }, [idPerfil]);

  return { perfilOptions, perfilData, loadingPerfil };
}

export default usePerfilAutoFill;
